import { useState, useRef, useEffect } from 'react'
import { Input } from 'react-daisyui'
import { IconPlus } from '@tabler/icons-react'
import { ProfTag } from '@/models/profTag.ts'
import TagBadge from '@/components/portal/TagBadge.tsx'

const DEFAULT_TAG_COLOR = '#7EB6E6'

interface TagInputProps {
  selectedTags: ProfTag[]
  availableTags: ProfTag[]
  onAddTag: (tag: ProfTag) => void
  onRemoveTag: (tag: ProfTag) => void
  onCreateTag: (name: string) => Promise<ProfTag>
  onEditTag?: (tag: ProfTag) => void
}

export default function TagInput({ selectedTags, availableTags, onAddTag, onRemoveTag, onCreateTag, onEditTag }: TagInputProps) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedIds = new Set(selectedTags.map(t => t.id))
  const filtered = availableTags
    .filter(t => !selectedIds.has(t.id))
    .filter(t => t.name.toLowerCase().includes(query.toLowerCase()))

  const exactMatch = availableTags.some(t => t.name.toLowerCase() === query.trim().toLowerCase())
  const showCreate = query.trim().length > 0 && !exactMatch

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function handleSelect(tag: ProfTag) {
    onAddTag(tag)
    setQuery('')
    setIsOpen(false)
  }

  async function handleCreate() {
    if (isCreating) return
    setIsCreating(true)
    try {
      const newTag = await onCreateTag(query.trim())
      onAddTag(newTag)
      setQuery('')
      setIsOpen(false)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      {selectedTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedTags.map(tag => (
            <TagBadge
              key={tag.id}
              tag={tag}
              onRemove={() => onRemoveTag(tag)}
              onColorClick={onEditTag ? () => onEditTag(tag) : undefined}
            />
          ))}
        </div>
      )}

      <Input
        size="sm"
        className="w-full"
        placeholder="Tag suchen oder erstellen..."
        value={query}
        onChange={e => { setQuery(e.target.value); setIsOpen(true) }}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && (filtered.length > 0 || showCreate) && (
        <div className="absolute z-50 mt-1 w-full bg-secondary border border-base-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.map(tag => (
            <button
              key={tag.id}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left text-sm"
              onClick={() => handleSelect(tag)}
            >
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: tag.color ? `#${tag.color}` : DEFAULT_TAG_COLOR }}
              />
              {tag.name}
            </button>
          ))}
          {showCreate && (
            <button
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-secondary text-left text-sm text-primary"
              onClick={handleCreate}
              disabled={isCreating}
            >
              <IconPlus size={14} />
              Tag erstellen: „{query.trim()}"
            </button>
          )}
        </div>
      )}
    </div>
  )
}
